import { useState } from 'react';
import { matchCertificationService } from '../utils/certificationService';

export const usePasteHandler = ({ rows, setRows, selectedCells, services, fields }) => {
  const [isPasting, setIsPasting] = useState(false);

  const cleanCurrencyValue = (value) => {
    // Remove currency symbols, commas, and whitespace, then convert to number
    return value.replace(/[$,\s]/g, '');
  };

  const handlePaste = (e, rowIndex, field) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text');
    const pastedRows = pastedData.split('\n').filter(row => row.trim());

    // Handle multi-row paste
    if (pastedRows.length > 1) {
      handleMultiRowPaste(pastedRows, rowIndex, field);
      return;
    }

    // Handle single cell paste with selected cells
    if (selectedCells.length > 0 && selectedCells.every(cell => cell.field === field)) {
      handleSelectedCellsPaste(pastedData, field);
      return;
    }

    // Handle tab-separated data
    if (pastedData.includes('\t')) {
      handleTabSeparatedPaste(pastedData, rowIndex);
      return;
    }

    // Single cell paste
    handleSingleCellPaste(pastedData, rowIndex, field);
  };

  const handleMultiRowPaste = (pastedRows, rowIndex, field) => {
    // Create new rows for the pasted data
    const updatedRows = pastedRows.map((value, index) => {
      // Start with empty row or existing row data if available
      const existingRow = index === 0 ? { ...rows[rowIndex] } : {};
      
      // Handle certification service names
      if (field === 'certification_service_id') {
        const service = matchCertificationService(value, services);
        if (service) {
          return {
            ...existingRow,
            certification_service_id: service.id
          };
        }
      }

      // Clean currency values for price fields
      const cleanedValue = field.includes('price') || field === 'fees' || field === 'grading_cost' || field === 'shipping_cost'
        ? cleanCurrencyValue(value)
        : value.trim();
      
      return {
        ...existingRow,
        [field]: cleanedValue
      };
    });

    // Preserve existing rows and merge in new rows
    setRows(prevRows => {
      const beforeRows = prevRows.slice(0, rowIndex);
      const afterRows = prevRows.slice(rowIndex + updatedRows.length);
      
      // For each new row, merge with corresponding existing row if it exists
      const mergedRows = updatedRows.map((newRow, i) => {
        const existingRow = prevRows[rowIndex + i];
        return existingRow ? { ...existingRow, ...newRow } : newRow;
      });

      return [...beforeRows, ...mergedRows, ...afterRows];
    });
  };

  const handleSelectedCellsPaste = (pastedData, field) => {
    // Sort selected cells by row index to maintain order
    const sortedCells = [...selectedCells].sort((a, b) => a.rowIndex - b.rowIndex);
    
    // Use the entire clipboard data as a single value for all selected cells
    let value = pastedData.trim();
    
    // Clean currency values for price fields
    if (field.includes('price') || field === 'fees' || field === 'grading_cost' || field === 'shipping_cost') {
      value = cleanCurrencyValue(value);
    }
    
    // Update all rows at once
    setRows(prevRows => {
      const newRows = [...prevRows];
      sortedCells.forEach(cell => {
        if (field === 'certification_service_id') {
          const service = matchCertificationService(value, services);
          if (service) {
            newRows[cell.rowIndex] = {
              ...newRows[cell.rowIndex],
              certification_service_id: service.id
            };
          }
        } else {
          newRows[cell.rowIndex] = {
            ...newRows[cell.rowIndex],
            [field]: value
          };
        }
      });
      return newRows;
    });
  };

  const handleTabSeparatedPaste = (pastedData, rowIndex) => {
    if (!fields?.length) return;
    
    const newRows = pastedData.split('\n').map(row => {
      const values = row.split('\t');
      const rowData = {};
      values.forEach((value, index) => {
        const field = fields[index];
        if (field) {
          if (field.key === 'certification_service_id') {
            const service = matchCertificationService(value, services);
            if (service) {
              rowData.certification_service_id = service.id;
            }
          } else {
            // Clean currency values for price fields
            const cleanedValue = field.key.includes('price') || field.key === 'fees' || field.key === 'grading_cost' || field.key === 'shipping_cost'
              ? cleanCurrencyValue(value)
              : value.trim();
            rowData[field.key] = cleanedValue;
          }
        }
      });
      return rowData;
    });

    // Only update if we have valid data
    if (newRows.some(row => Object.keys(row).length > 0)) {
      setRows(prevRows => [...prevRows.slice(0, rowIndex), ...newRows]);
    }
  };

  const handleSingleCellPaste = (pastedData, rowIndex, field) => {
    if (field === 'certification_service_id') {
      const service = matchCertificationService(pastedData, services);
      if (service) {
        setRows(prevRows => {
          const newRows = [...prevRows];
          newRows[rowIndex] = {
            ...newRows[rowIndex],
            certification_service_id: service.id
          };
          return newRows;
        });
        return;
      }
    }

    // Clean currency values for price fields
    const value = field.includes('price') || field === 'fees' || field === 'grading_cost' || field === 'shipping_cost'
      ? cleanCurrencyValue(pastedData)
      : pastedData.trim();

    setRows(prevRows => {
      const newRows = [...prevRows];
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [field]: value
      };
      return newRows;
    });
  };

  return {
    isPasting,
    handlePaste
  };
};
